import { translateLocalized, useI18n } from '../client';
import type { LocalizedOrNode } from '../client';
import { Icon } from '@iconify/react';
import React, { forwardRef, useId, useMemo } from "react";
import { Controller, type FieldPath, type FieldValues, type RegisterOptions, type ValidationRule, type Message } from "react-hook-form";
import { useFormContext } from "./context";
import { useFieldUpdate } from "./hook";

// Global counter for unique IDs
let globalIdCounter = 0;

export interface FormComponentProps {
    name: string;
    isRequired?: boolean;
    canClear?: boolean;
    localizedPlaceholder?: LocalizedOrNode;
    rules?: Omit<RegisterOptions<FieldValues, FieldPath<FieldValues>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
    defaultValue?: unknown;
    errorMessage?: string;
}

function hasOnClear<P extends object>(Component: React.ComponentType<P>): boolean {
    const supportedComponents = [
        "NextUI.Input",
        "NextUI.Select",
        "NextUI.Autocomplete",
        "NextUI.Textarea"
    ];
    return supportedComponents.includes(Component.displayName || "");
}

function supportsPasswordToggle<P extends object>(Component: React.ComponentType<P>): boolean {
    return Component.displayName === "HeroUI.Input";
}

function supportsFormProps<P extends object>(Component: React.ComponentType<P>): boolean {
    const supportedComponents = [
        "HeroUI.Input",
        "HeroUI.Select",
        "HeroUI.Autocomplete",
        "HeroUI.Textarea",
        "HeroUI.Checkbox",
        "HeroUI.CheckboxGroup",
        "HeroUI.RadioGroup",
        "HeroUI.Switch",
        "HeroUI.DatePicker",
        "HeroUI.TimeInput"
    ];
    return supportedComponents.includes(Component.displayName || "");
}

function supportsErrorMessage<P extends object>(Component: React.ComponentType<P>): boolean {
    const supportedComponents = [
        "HeroUI.Input",
        "HeroUI.Select",
        "HeroUI.Autocomplete",
        "HeroUI.Textarea",
        "HeroUI.CheckboxGroup",
        "HeroUI.RadioGroup",
        "HeroUI.DatePicker",
        "HeroUI.TimeInput"
    ];
    return supportedComponents.includes(Component.displayName || "");
}

function getDefaultValue<P extends object>(Component: React.ComponentType<P>, customDefault?: unknown): unknown {
    const componentName = Component.displayName || "";

    if (customDefault !== undefined) {
        return customDefault;
    }

    switch (componentName) {
        case "NextUI.Switch":
            return false;
        case "NextUI.Checkbox":
            return false;
        case "NextUI.CheckboxGroup":
            return [];
        case "NextUI.RadioGroup":
            return "";
        case "NextUI.Select":
            return new Set([]);
        case "NextUI.Slider":
            return 0;
        case "NextUI.DatePicker":
            return null;
        case "NextUI.TimeInput":
            return null;
        default:
            return "";
    }
}

export function withFormComponent<P extends object>(Component: React.ComponentType<P>) {
    type Props = P & FormComponentProps;

    const WrappedComponent = forwardRef<HTMLElement, Props>(({ name, rules, isRequired, localizedPlaceholder, canClear, defaultValue, errorMessage, ...props }, ref) => {

        const { control } = useFormContext();
        const { clear, isInvalid, handle } = useFieldUpdate(name);
        const [isVisible, setIsVisible] = React.useState(false);
        const toggleVisibility = () => setIsVisible(!isVisible);
        const { t } = useI18n();
        const reactId = useId();

        // Create truly unique ID using global counter + reactId + timestamp
        const uniqueId = useMemo(() => {
            globalIdCounter += 1;
            return `${globalIdCounter}-${reactId.replace(/:/g, '')}-${Date.now()}`;
        }, [reactId]);

        const computedDefaultValue = getDefaultValue(Component, defaultValue);

        const computedRequired: Message | ValidationRule<boolean> = useMemo(() => {
            if (isRequired) {
                const baseRule: Message | ValidationRule<boolean> = {
                    value: true,
                    message: '此字段为必填项'
                };

                if (rules?.required !== undefined) {
                    // 处理 Message (string) 类型
                    if (typeof rules.required === 'string') {
                        return {
                            ...baseRule,
                            message: rules.required
                        };
                    }
                    // 处理 boolean 类型
                    else if (typeof rules.required === 'boolean') {
                        return baseRule;
                    }
                    // 处理 ValidationValueMessage<boolean> 对象类型 { value: boolean; message: Message }
                    else if (typeof rules.required === 'object' && rules.required !== null) {
                        return {
                            ...baseRule,
                            ...rules.required
                        };
                    }
                }

                return baseRule;
            }

            // 当 isRequired 为 false 时，直接返回原始的 rules.required
            return rules?.required || false;
        }, [isRequired, rules?.required]);

        return (
            <Controller
                name={name}
                control={control}
                defaultValue={computedDefaultValue}
                rules={{
                    ...rules,
                    required: computedRequired
                }}
                render={({ field: { onChange, onBlur, value, ref: fieldRef }, fieldState: { error } }) => {
                    const componentProps: Record<string, unknown> = {
                        ref: ref || fieldRef,
                        value: value ?? computedDefaultValue,
                        onBlur: handle(onBlur),
                        onChange: handle(onChange),
                        id: `${name}-${uniqueId}`,
                        ...props
                    };

                    // 提取 key 属性，避免 React 警告
                    let componentKey: string | undefined;

                    // 只对支持表单属性的组件传递 isRequired 和 isInvalid
                    if (supportsFormProps(Component)) {
                        componentProps.isRequired = isRequired;
                        // 修复：同时考虑react-hook-form的错误状态和自定义验证
                        componentProps.isInvalid = !!error || (isRequired && isInvalid());

                        // 关键修复：只对支持errorMessage属性的组件传递错误信息
                        if (supportsErrorMessage(Component) && (!!error || (isRequired && isInvalid()))) {
                            componentProps.errorMessage = error?.message || errorMessage || '此字段为必填项';
                        }
                    }

                    if (Component.displayName === "HeroUI.Autocomplete") {
                        const baseId = `${name}-${uniqueId}`;
                        componentKey = baseId; // 提取 key，不放在 componentProps 中

                        componentProps.selectorButtonProps = {
                            id: `${baseId}-selector-btn`,
                            'aria-label': `Show ${componentProps.label || name} suggestions`,
                            ...(typeof componentProps.selectorButtonProps === 'object' && componentProps.selectorButtonProps !== null ? componentProps.selectorButtonProps : {})
                        };

                        componentProps.inputProps = {
                            id: `${baseId}-input`,
                            ...(typeof componentProps.inputProps === 'object' && componentProps.inputProps !== null ? componentProps.inputProps : {})
                        };

                        componentProps.className = `${componentProps.className || ''} autocomplete-instance-${uniqueId}`.trim();
                    }

                    if (hasOnClear(Component) && canClear) {
                        componentProps.onClear = clear;
                    }

                    if (supportsPasswordToggle(Component) && "type" in props && props.type === "password") {
                        componentProps.endContent = (
                            <button className="focus:outline-none" type="button" onClick={toggleVisibility} aria-label="toggle password visibility">
                                {isVisible ? (
                                    <Icon icon="solar:eye-bold" />
                                ) : (
                                    <Icon icon="solar:eye-closed-linear" />
                                )}
                            </button>
                        );
                        componentProps.type = isVisible ? "text" : "password";
                    }

                    if (hasOnClear(Component) && localizedPlaceholder) {
                        componentProps.placeholder = translateLocalized(localizedPlaceholder, t);
                    }

                    // 对于不支持errorMessage的组件，我们需要在外层包装错误显示
                    const hasError = !!error || (isRequired && isInvalid());
                    const errorMsg = error?.message || errorMessage || '此字段为必填项';
                    const needsExternalErrorDisplay = supportsFormProps(Component) && !supportsErrorMessage(Component) && hasError;

                    const componentElement = (
                        <Component
                            key={componentKey}
                            {...(componentProps as P)}
                        />
                    );

                    return needsExternalErrorDisplay ? (
                        <div className="space-y-1">
                            {componentElement}
                            <p className="text-sm text-danger">{errorMsg}</p>
                        </div>
                    ) : (
                        componentElement
                    );
                }}
            />
        );
    });

    WrappedComponent.displayName = `withFormComponent(${Component.displayName || Component.name || "Component"})`;

    return WrappedComponent;
}
